// Hours: the controls for time ranges, drawn compactly wherever a schema has their shape — a week of
// opening hours, one day's list of ranges, a holiday's date with closed or special hours. Recognised
// by structure (`settings/schema.ts`), so a registry setting of the same shape gets them too.
//
//   week       a summary line, the timezone and a quick setup; then one row per day: its name, Open or Closed,
//              the ranges inline (or Open 24 hours), a break for a split shift, and apply to all days
//   ranges     `10:00 → 02:00 next day`: a range that closes before it opens runs past midnight
//   hours-row  a flag (a holiday's `closed`) with the ranges it switches off, and its other fields

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Checkbox, CloseIcon, Field, Input, SegmentedControl, Switch, VisuallyHidden } from '@clavian/ui-web';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { requestRevealSetting } from './reveal';
import { hoursRowOf, propertiesOf, rangesItemOf, weekDaysOf, type JsonSchema, type RangeShape } from './schema';
import { errorKey, type ControlProps } from './types';

type Range = Readonly<Record<string, unknown>>;

const namesOnly = (path: readonly (string | number)[]): string[] =>
  path.filter((segment): segment is string => typeof segment === 'string');

const asRanges = (value: unknown): Range[] =>
  Array.isArray(value) ? value.filter((item): item is Range => typeof item === 'object' && item !== null) : [];

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const blank = (shape: RangeShape): Range => ({ [shape.start]: '', [shape.end]: '' });

const TIME = /^\d{2}:\d{2}$/;

// ---------------------------------------------------------------------------------------------
// The ranges of one day
// ---------------------------------------------------------------------------------------------

interface RangesEditorProps {
  readonly control: ControlProps;
  readonly shape: RangeShape;
  /** Where the ranges are in the value: their errors are filed under it. */
  readonly path: readonly (string | number)[];
  /** The day or date the ranges belong to, for their controls' names: "Monday opens". */
  readonly rowLabel: string;
  readonly ranges: readonly Range[];
  readonly maxItems: number;
  readonly onChange: (ranges: Range[]) => void;
  /** Said when there are no ranges. */
  readonly emptyText?: string | undefined;
  /** Draw the add button below the ranges; the week draws it in its row's actions instead. */
  readonly showAdd?: boolean;
  /** The fewest ranges it keeps: a week's open day keeps one (Closed is how a day loses its hours). */
  readonly minItems?: number;
}

function RangesEditor({ control, shape, path, rowLabel, ranges, maxItems, onChange, emptyText, showAdd = true, minItems = 0 }: RangesEditorProps) {
  const { t } = useTranslation('console');
  const id = useId();
  const startLabel = control.labels.field(control.entryKey, [...namesOnly(path), shape.start]).toLocaleLowerCase();
  const endLabel = control.labels.field(control.entryKey, [...namesOnly(path), shape.end]).toLocaleLowerCase();

  const set = (index: number, key: string, time: string) =>
    onChange(ranges.map((range, at) => (at === index ? { ...range, [key]: time } : { ...range })));

  return (
    <div className="console-ranges">
      {ranges.length === 0 && emptyText ? <p className="console-muted">{emptyText}</p> : null}
      {ranges.map((range, index) => {
        const start = typeof range[shape.start] === 'string' ? (range[shape.start] as string) : '';
        const end = typeof range[shape.end] === 'string' ? (range[shape.end] as string) : '';
        const both = TIME.test(start) && TIME.test(end);
        const note = both && end < start ? t('settings.hours.nextDay') : both && end === start && shape.allDayWhenEqual ? t('settings.hours.allDay') : undefined;
        const which = ranges.length > 1 ? t('settings.hours.rangeNumber', { n: index + 1 }) : '';
        const startError = control.errorAt([...path, index, shape.start]);
        const endError = control.errorAt([...path, index, shape.end]);
        const errorId = `${id}-${index}-error`;
        const noteId = `${id}-${index}-note`;
        return (
          <div className="console-range" key={index}>
            <Input
              type="time"
              numeric
              aria-label={`${rowLabel} ${startLabel}${which}`}
              value={start}
              disabled={control.disabled}
              aria-invalid={startError ? true : undefined}
              aria-describedby={startError ? errorId : undefined}
              onChange={(event) => set(index, shape.start, event.target.value)}
            />
            <span className="console-range__arrow" aria-hidden="true">
              →
            </span>
            <Input
              type="time"
              numeric
              aria-label={`${rowLabel} ${endLabel}${which}`}
              value={end}
              disabled={control.disabled}
              aria-invalid={endError ? true : undefined}
              aria-describedby={[note ? noteId : '', endError ? errorId : ''].filter(Boolean).join(' ') || undefined}
              onChange={(event) => set(index, shape.end, event.target.value)}
            />
            {note ? (
              <span className="console-range__note" id={noteId}>
                {note}
              </span>
            ) : null}
            {control.disabled || ranges.length <= minItems ? null : (
              <Button
                size="sm"
                variant="ghost"
                icon={<CloseIcon />}
                label={t('settings.hours.removeRange', { row: rowLabel, n: index + 1 })}
                onClick={() => onChange(ranges.filter((_, at) => at !== index).map((kept) => ({ ...kept })))}
              />
            )}
            {startError || endError ? (
              <p className="clav-field__error console-range__error" id={errorId}>
                {[startError, endError].filter(Boolean).join(' ')}
              </p>
            ) : null}
          </div>
        );
      })}
      {!showAdd || control.disabled || ranges.length >= maxItems ? null : (
        <div>
          <AddRangeButton rowLabel={rowLabel} first={ranges.length === 0} onAdd={() => onChange([...ranges.map((range) => ({ ...range })), blank(shape)])} />
        </div>
      )}
    </div>
  );
}

function AddRangeButton({ rowLabel, first, onAdd }: { readonly rowLabel: string; readonly first: boolean; readonly onAdd: () => void }) {
  const { t } = useTranslation('console');
  return (
    <Button size="sm" variant="ghost" onClick={onAdd}>
      {first ? t('settings.hours.addRange') : t('settings.hours.anotherRange')}
      {/* The space is outside: a name is joined from its parts, and the parts are trimmed. */}{' '}
      <VisuallyHidden>{t('settings.hours.onRow', { row: rowLabel })}</VisuallyHidden>
    </Button>
  );
}

function maxItemsOf(schema: JsonSchema | undefined): number {
  const max = schema?.['maxItems'];
  return typeof max === 'number' ? max : Infinity;
}

// ---------------------------------------------------------------------------------------------
// A list of ranges on its own
// ---------------------------------------------------------------------------------------------

export function TimeRangesControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const shape = rangesItemOf(props.schema, props.context) as RangeShape;
  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <RangesEditor
          control={props}
          shape={shape}
          path={props.path}
          rowLabel={props.label}
          ranges={asRanges(props.value)}
          maxItems={maxItemsOf(props.schema)}
          onChange={props.onChange}
          emptyText={t('settings.control.empty')}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------
// A week (spec/13 §13, redesigned 2026-10-07)
//
// Each day reads as a sentence: its name, Open or Closed, and when open its times — "10:00 – 02:00 next day" — or
// Open 24 hours. Above the days: a one-line summary of the week ("Open 7 days · 10 am – 2 am"), the timezone the
// times are in, and a quick setup — the same hours every day, or a common pattern to start from. Under them: what a
// closing time after midnight means, and how it drives the closing warnings and the closing-time rule.
//
// What the contract's `OpeningHours` can say, and nothing more: up to four ranges a day; a range that closes before it
// opens runs past midnight into the next day and belongs to the day it opens on; the same time twice is open 24
// hours. A day with an empty time is not a value yet: it says so in plain words and the form will not save it.
// ---------------------------------------------------------------------------------------------

const ALL_DAY: Range = { opens: '00:00', closes: '00:00' };

/** A range whose two times are equal: open 24 hours (the contract's `OpeningInterval`). */
const isAllDay = (shape: RangeShape, range: Range): boolean =>
  typeof range[shape.start] === 'string' && range[shape.start] !== '' && range[shape.start] === range[shape.end];

/** Common weeks to start from (`settings.hours.preset`). */
const PRESETS: readonly { readonly id: string; readonly week: (days: readonly string[]) => Record<string, Range[]> }[] = [
  { id: 'lateNight', week: (days) => Object.fromEntries(days.map((day) => [day, [{ opens: '10:00', closes: '02:00' }]])) },
  { id: 'allDay', week: (days) => Object.fromEntries(days.map((day) => [day, [{ ...ALL_DAY }]])) },
  {
    id: 'weekdaysWeekends',
    week: (days) =>
      Object.fromEntries(
        days.map((day, index) => [day, [index >= days.length - 2 ? { opens: '10:00', closes: '02:00' } : { opens: '12:00', closes: '00:00' }]]),
      ),
  },
];

/** "10:00" → "10 am", "12:30" → "12:30 pm", in the reader's language. */
export function clockText(time: string, language: string): string {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (match === null) return time;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const date = new Date(Date.UTC(2000, 0, 1, hours, minutes));
  return new Intl.DateTimeFormat(language, { hour: 'numeric', ...(minutes === 0 ? {} : { minute: '2-digit' }), timeZone: 'UTC' }).format(date);
}

export interface WeekSummaryWords {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly language: string;
  /** A day's short name: `mon` → "Mon". */
  readonly dayShort: (day: string) => string;
}

/**
 * The week in one line: "Open 7 days · 10 am – 2 am", "Mon–Fri 12 pm – 12 am · Sat–Sun 10 am – 2 am",
 * "Mon–Sat 11 am – 11 pm · Sun Closed". Consecutive days with the same hours share one part.
 */
export function weekSummary(week: Readonly<Record<string, readonly Range[]>>, days: readonly string[], shape: RangeShape, words: WeekSummaryWords): string {
  const { t } = words;
  const hoursOf = (ranges: readonly Range[]): string => {
    if (ranges.length === 0) return t('settings.hours.summary.closed');
    if (ranges.length === 1 && ranges[0] !== undefined && isAllDay(shape, ranges[0])) return t('settings.hours.summary.allDay');
    return ranges
      .map((range) => t('settings.hours.summary.range', { from: clockText(String(range[shape.start] ?? ''), words.language), to: clockText(String(range[shape.end] ?? ''), words.language) }))
      .join(t('settings.hours.summary.and'));
  };
  const keyOf = (ranges: readonly Range[]) => JSON.stringify(ranges.map((range) => [range[shape.start], range[shape.end]]));
  if (days.every((day) => (week[day] ?? []).length === 0)) return t('settings.hours.summary.closedAllWeek');
  const first = days[0];
  if (first !== undefined && days.every((day) => keyOf(week[day] ?? []) === keyOf(week[first] ?? []))) {
    return t('settings.hours.summary.everyDay', { hours: hoursOf(week[first] ?? []) });
  }
  const runs: { from: string; to: string; ranges: readonly Range[] }[] = [];
  for (const day of days) {
    const ranges = week[day] ?? [];
    const last = runs.at(-1);
    if (last !== undefined && keyOf(last.ranges) === keyOf(ranges)) last.to = day;
    else runs.push({ from: day, to: day, ranges });
  }
  return runs
    .map((run) =>
      t('settings.hours.summary.group', {
        days: run.from === run.to ? words.dayShort(run.from) : t('settings.hours.dayRange', { from: words.dayShort(run.from), to: words.dayShort(run.to) }),
        hours: hoursOf(run.ranges),
      }),
    )
    .join(t('settings.hours.summary.separator'));
}

/** The problems with a week, by day, in plain words: a missing time (blocking), the same time twice (a note). */
export function weekProblems(week: Readonly<Record<string, readonly Range[]>>, days: readonly string[], shape: RangeShape) {
  const missing: string[] = [];
  const sameTime: string[] = [];
  for (const day of days) {
    for (const range of week[day] ?? []) {
      const start = typeof range[shape.start] === 'string' ? (range[shape.start] as string) : '';
      const end = typeof range[shape.end] === 'string' ? (range[shape.end] as string) : '';
      if (!TIME.test(start) || !TIME.test(end)) missing.push(day);
      else if (start === end && start !== '00:00') sameTime.push(day);
    }
  }
  return { missing: [...new Set(missing)], sameTime: [...new Set(sameTime)] };
}

export function WeekControl(props: ControlProps) {
  const { t } = useTranslation('console');
  const { locale } = useVenueFormatter();
  const days = weekDaysOf(props.schema, props.context) ?? [];
  const properties = propertiesOf(props.schema);
  const week = asRecord(props.value);
  const hours = (day: string) => asRanges(week[day]);
  const labelOf = (day: string) => props.labels.field(props.entryKey, [...namesOnly(props.path), day]);
  const firstDay = days[0];
  const shape = (firstDay === undefined ? undefined : rangesItemOf(properties[firstDay] as JsonSchema, props.context)) ?? { start: 'opens', end: 'closes', allDayWhenEqual: true };
  const [same, setSame] = useState({ opens: '10:00', closes: '22:00' });

  const setWeek = (next: Record<string, unknown>) => props.onChange(next);
  const setDay = (day: string, ranges: Range[]) => setWeek({ ...week, [day]: ranges });

  /** A day that opens takes the hours of the nearest open day before it, or after it. */
  const hoursToOpenWith = (day: string): Range[] => {
    const at = days.indexOf(day);
    const nearest = [...days.slice(0, at).reverse(), ...days.slice(at + 1)].find((other) => hours(other).length > 0);
    return nearest === undefined ? [{ [shape.start]: '10:00', [shape.end]: '22:00' }] : hours(nearest).map((range) => ({ ...range }));
  };
  const applyToAll = (ranges: readonly Range[]) => setWeek(Object.fromEntries(days.map((other) => [other, ranges.map((range) => ({ ...range }))])));

  const problems = weekProblems(week as Record<string, readonly Range[]>, days, shape);
  // A day with an empty time is not a value yet: the form will not save it.
  const key = errorKey(props.entryKey, props.path);
  const { onUnparsed } = props;
  const blocked = problems.missing.length > 0;
  useEffect(() => {
    if (!blocked) return undefined;
    onUnparsed?.(key, true);
    return () => onUnparsed?.(key, false);
  }, [onUnparsed, key, blocked]);

  const summary = weekSummary(week as Record<string, readonly Range[]>, days, shape, {
    t: (id, options) => t(id, options ?? {}),
    language: locale,
    dayShort: (day) => t(`settings.hours.dayShort.${day}`, { defaultValue: labelOf(day).slice(0, 3) }),
  });
  const timezone = props.context.timezone;

  return (
    <Field as="group" label={props.label} description={undefined} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-week">
          <p className="console-week__summary" aria-live="polite">
            {summary}
          </p>
          {timezone === undefined ? null : <p className="console-week__timezone console-muted">{t('settings.hours.timezone', { timezone })}</p>}

          {props.disabled ? null : (
            <div className="console-week__quick" role="group" aria-label={t('settings.hours.quickTitle')}>
              <div className="console-week__same">
                <span className="console-week__quick-label">{t('settings.hours.sameEveryDay')}</span>
                <div className="console-range">
                  <Input type="time" numeric aria-label={t('settings.hours.sameOpens')} value={same.opens} onChange={(event) => setSame({ ...same, opens: event.target.value })} />
                  <span className="console-range__arrow" aria-hidden="true">
                    –
                  </span>
                  <Input type="time" numeric aria-label={t('settings.hours.sameCloses')} value={same.closes} onChange={(event) => setSame({ ...same, closes: event.target.value })} />
                  {TIME.test(same.opens) && TIME.test(same.closes) && same.closes < same.opens ? <span className="console-range__note">{t('settings.hours.nextDay')}</span> : null}
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!TIME.test(same.opens) || !TIME.test(same.closes)}
                  onClick={() => applyToAll([{ [shape.start]: same.opens, [shape.end]: same.closes }])}
                >
                  {t('settings.hours.applySame')}
                </Button>
              </div>
              <div className="console-week__presets">
                <span className="console-week__quick-label">{t('settings.hours.presets')}</span>
                {PRESETS.map((preset) => (
                  <button key={preset.id} type="button" className="clav-chip clav-chip--toggle" onClick={() => setWeek(preset.week(days))}>
                    {t(`settings.hours.preset.${preset.id}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="console-week__days">
            {days.map((day) => {
              const ranges = hours(day);
              const open = ranges.length > 0;
              const allDay = ranges.length === 1 && ranges[0] !== undefined && isAllDay(shape, ranges[0]) && ranges[0][shape.start] === '00:00';
              const dayLabel = labelOf(day);
              const dayShape = rangesItemOf(properties[day] as JsonSchema, props.context) ?? shape;
              return (
                <div className="console-day" key={day} data-open={open || undefined}>
                  <span className="console-day__name" id={`${key}-${day}`}>
                    {dayLabel}
                  </span>
                  <SegmentedControl<'open' | 'closed'>
                    className="console-day__state"
                    size="sm"
                    label={t('settings.hours.dayState', { day: dayLabel })}
                    value={open ? 'open' : 'closed'}
                    options={[
                      { value: 'open', label: t('settings.hours.open'), disabled: props.disabled },
                      { value: 'closed', label: t('settings.hours.closed'), disabled: props.disabled },
                    ]}
                    onValueChange={(next) => setDay(day, next === 'open' ? hoursToOpenWith(day) : [])}
                  />
                  <div className="console-day__hours">
                    {!open ? null : allDay ? (
                      <p className="console-day__allday">{t('settings.hours.open24')}</p>
                    ) : (
                      <RangesEditor
                        control={props}
                        shape={dayShape}
                        path={[...props.path, day]}
                        rowLabel={dayLabel}
                        ranges={ranges}
                        maxItems={maxItemsOf(properties[day])}
                        onChange={(next) => setDay(day, next)}
                        showAdd={false}
                        minItems={1}
                      />
                    )}
                  </div>
                  {open && !props.disabled ? (
                    <div className="console-day__actions">
                      <Checkbox
                        label={
                          <>
                            {t('settings.hours.open24')}
                            {/* The space is outside: a name is joined from its parts, and the parts are trimmed. */}{' '}
                            <VisuallyHidden>{t('settings.hours.onRow', { row: dayLabel })}</VisuallyHidden>
                          </>
                        }
                        checked={allDay}
                        onCheckedChange={(checked) => {
                          if (checked === true) setDay(day, [{ ...ALL_DAY }]);
                          else {
                            const nearest = days.map(hours).find((other) => other.length > 0 && !(other.length === 1 && other[0] !== undefined && isAllDay(shape, other[0])));
                            setDay(day, nearest?.map((range) => ({ ...range })) ?? [{ [shape.start]: '10:00', [shape.end]: '22:00' }]);
                          }
                        }}
                      />
                      {!allDay && ranges.length < maxItemsOf(properties[day]) ? (
                        <AddRangeButton rowLabel={dayLabel} first={false} onAdd={() => setDay(day, [...ranges.map((range) => ({ ...range })), blank(dayShape)])} />
                      ) : null}
                      <Button size="sm" variant="ghost" onClick={() => applyToAll(ranges)}>
                        {t('settings.hours.applyToAll')}
                        {' '}
                        <VisuallyHidden>{t('settings.hours.fromRow', { row: dayLabel })}</VisuallyHidden>
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {problems.missing.length > 0 || problems.sameTime.length > 0 ? (
            <ul className="console-week__problems">
              {problems.missing.map((day) => (
                <li key={`missing-${day}`} className="clav-field__error">
                  {t('settings.hours.missingTime', { day: labelOf(day) })}
                </li>
              ))}
              {problems.sameTime.map((day) => (
                <li key={`same-${day}`} className="console-muted">
                  {t('settings.hours.sameTime', { day: labelOf(day) })}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="console-week__note console-muted">
            <p>
              {t('settings.hours.overnight')}{' '}
              <a
                href="#venue.closingAction"
                onClick={(event) => {
                  event.preventDefault();
                  requestRevealSetting('venue.closingAction');
                }}
              >
                {t('settings.hours.closingLink')}
              </a>
              .
            </p>
            <p>{t('settings.hours.businessDay')}</p>
          </div>
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------
// One day with a switch: a holiday's date, closed, or special hours
// ---------------------------------------------------------------------------------------------

export function HoursRowControl(props: ControlProps & { readonly renderFields: (names: readonly string[]) => ReactNode }) {
  const shape = hoursRowOf(props.schema, props.context);
  const properties = propertiesOf(props.schema);
  const value = asRecord(props.value);
  if (shape === undefined) return null;
  const rangeShape = rangesItemOf(properties[shape.ranges] as JsonSchema, props.context) as RangeShape;
  const flagOn = value[shape.flag] === true;
  const hoursShown = shape.flagCloses ? !flagOn : flagOn;
  const names = namesOnly(props.path);
  const rangesPath = [...props.path, shape.ranges];
  const rowLabel =
    [value['name'], value['date']].find((part): part is string => typeof part === 'string' && part.trim() !== '') ?? props.label;

  return (
    <Field as="group" label={props.label} description={props.description} error={props.errorAt(props.path)}>
      {() => (
        <div className="console-hours-row">
          {props.renderFields(shape.others)}
          <Switch
            label={props.labels.field(props.entryKey, [...names, shape.flag])}
            checked={flagOn}
            disabled={props.disabled}
            onCheckedChange={(on) => {
              // A day that shuts has no hours: the contract refuses a closed holiday with any.
              const shuts = shape.flagCloses ? on : !on;
              props.onChange({ ...value, [shape.flag]: on, ...(shuts ? { [shape.ranges]: [] } : {}) });
            }}
          />
          {hoursShown ? (
            <div className="console-hours-row__hours">
              <p className="console-hours-row__label">{props.labels.field(props.entryKey, [...names, shape.ranges])}</p>
              <RangesEditor
                control={props}
                shape={rangeShape}
                path={rangesPath}
                rowLabel={rowLabel}
                ranges={asRanges(value[shape.ranges])}
                maxItems={maxItemsOf(properties[shape.ranges])}
                onChange={(next) => props.onChange({ ...value, [shape.ranges]: next })}
                emptyText={props.labels.description(props.entryKey, [...names, shape.ranges], properties[shape.ranges] ?? {})}
              />
            </div>
          ) : null}
        </div>
      )}
    </Field>
  );
}
